'use client';

import React from 'react';
import { Box, Flex, useBreakpointValue } from '@chakra-ui/react';

import { Sidebar, SidebarProps } from './sidebar';

export interface ShellProps {
  children: React.ReactNode;
  /**
   * Sidebar props to pass through
   */
  sidebarProps?: Partial<SidebarProps>;
  /**
   * Header/TopBar component
   */
  header?: React.ReactNode;
  /**
   * Skip to content link text (for accessibility)
   */
  skipToContentText?: string;
  /**
   * Main content ID for skip link
   */
  contentId?: string;
  /**
   * Background color for main content area
   */
  backgroundColor?: string;
  /**
   * Whether to show the sidebar
   */
  showSidebar?: boolean;
  /**
   * Custom sidebar component (replaces default Sidebar)
   */
  customSidebar?: React.ReactNode;
}

export function Shell({
  children,
  sidebarProps,
  header,
  skipToContentText = 'Skip to content',
  contentId = 'main-content',
  backgroundColor = '#F8F9FA',
  showSidebar = true,
  customSidebar,
}: ShellProps) {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [sidebarOpen, setSidebarOpen] = React.useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);

  // Handle sidebar toggle for mobile
  const handleToggle = React.useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  // Handle sidebar collapse for desktop
  const handleCollapse = React.useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  return (
    <Box minHeight="100vh" backgroundColor={backgroundColor}>
      {/* Skip to content link for accessibility */}
      <a
        href={`#${contentId}`}
        style={{
          position: 'absolute',
          left: '-9999px',
          top: '8px',
          zIndex: 999,
          padding: '8px',
          backgroundColor: '#0066CC',
          color: 'white',
          textDecoration: 'none',
        }}
        onFocus={(e) => {
          e.currentTarget.style.left = '8px';
        }}
        onBlur={(e) => {
          e.currentTarget.style.left = '-9999px';
        }}
      >
        {skipToContentText}
      </a>

      <Flex height="100vh" overflow="hidden">
        {/* Sidebar */}
        {showSidebar && (
          <>
            {customSidebar || (
              <Sidebar
                open={isMobile ? sidebarOpen : true}
                collapsed={!isMobile && sidebarCollapsed}
                onToggle={handleToggle}
                onCollapse={handleCollapse}
                {...sidebarProps}
              />
            )}
          </>
        )}

        {/* Main Content Area */}
        <Flex
          flex="1"
          flexDirection="column"
          overflow="hidden"
          width="100%"
          marginLeft={
            showSidebar && !isMobile && !customSidebar ? (sidebarCollapsed ? '64px' : '280px') : '0'
          }
          transition="margin-left 0.2s ease"
        >
          {/* Header */}
          {header && (
            <Box
              as="header"
              position="sticky"
              top="0"
              zIndex="sticky"
              backgroundColor="white"
              borderBottomWidth="1px"
              borderColor="#E9ECEF"
              boxShadow="sm"
            >
              {header}
            </Box>
          )}

          {/* Main Content */}
          <Box as="main" id={contentId} flex="1" overflow="auto" backgroundColor={backgroundColor}>
            {children}
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
}

// Export with display name for debugging
Shell.displayName = 'Shell';
